import React, { FunctionComponent, useEffect, useState } from 'react'
import { reactular } from '@vendhq/reactular'
import { LoaderSpinner, Scrollable } from '@lightspeed/design-system-react'
import { useAppSelector } from '../../../state/hooks'
import {
  selectSaleDisplayStatus,
  selectSaleStatus,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { isQuote, shouldDisplaySaleNote } from '../../../functions/sale'
import {
  useRegisterManager,
  useTrainingModeManager,
  useVdEscapeStack,
} from '../../../ng-compat/hooks'
import { NoteError } from '../../shared/NoteInput'
import { PaymentRequestSummaryCard } from '../payment-panel/PaymentRequest/PaymentRequestSummaryCard'
import { usePaymentRequests } from '../payment-panel/PaymentRequest/hooks/usePaymentRequests'
import { getFeatureFromCache } from '../../../util/feature-cache'
import {
  FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS,
  FEATURE_ENABLE_INVOICING_QUOTE_PAYMENT_REQUESTS,
} from '../../../constants'
import {
  QuotePaymentRequestCard,
  QuotePaymentRequestState,
} from './QuotePaymentRequestCard'
import { SaleActionBar } from './SaleActionBar'
import { ConfirmationHeader } from './ConfirmationHeader'
import { PrintButtons } from './PrintButtons'
import { ConfirmationCustomer } from './ConfirmationCustomer'
import { MarkAsPickedUpToggle } from './MarkAsPickedUpToggle'
import { ConfirmationNote } from './ConfirmationNote'
import { useConfirmationInit } from './useConfirmationInit'
import { useCloseSaleConfirmation } from './useCloseSaleConfirmation'
import { useCompleteTutorial } from './useCompleteTutorial'

export const ConfirmationPanel: FunctionComponent = () => {
  const status = useAppSelector(selectSaleStatus)
  const displayStatus = useAppSelector(selectSaleDisplayStatus)
  const register = useRegisterManager().getActiveRegister()
  const [saleNoteHasError, setSaleNoteHasError] = useState(false)
  const trainingModeManager = useTrainingModeManager()
  const hasInit = useConfirmationInit()
  const [isPickedUp, setIsPickedUp] = useState(false)
  const closeSaleConfirmation = useCloseSaleConfirmation(
    isPickedUp,
    setIsPickedUp
  )
  const completeTutorial = useCompleteTutorial()
  const escapeStack = useVdEscapeStack()
  const showSaleNote = shouldDisplaySaleNote(register, { displayStatus })
  const isQuoteSale = isQuote(status)

  const [
    quotePaymentRequest,
    setQuotePaymentRequest,
  ] = useState<QuotePaymentRequestState>({
    isToggleEnabled: false,
    amount: '',
    note: '',
    email: '',
  })

  const isPaymentRequestFeatureEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS
  )
  const isQuotePaymentRequestFeatureEnabled =
    isPaymentRequestFeatureEnabled &&
    getFeatureFromCache(FEATURE_ENABLE_INVOICING_QUOTE_PAYMENT_REQUESTS)
  const {
    paymentRequests,
    isLoading: isLoadingPaymentRequests,
    refetch: refetchPaymentRequests,
  } = usePaymentRequests()

  useEffect(() => {
    if (
      isPaymentRequestFeatureEnabled &&
      !isQuoteSale &&
      refetchPaymentRequests
    ) {
      refetchPaymentRequests()
    }
  }, [isPaymentRequestFeatureEnabled, isQuoteSale, refetchPaymentRequests])

  useEffect(() => {
    const handleComplete = () => {
      if (trainingModeManager.trainingModeOnboardingState) {
        completeTutorial()
      } else {
        closeSaleConfirmation('escape')
      }
    }

    escapeStack.register(handleComplete)

    return () => {
      escapeStack.deregister(handleComplete)
    }
  }, [
    closeSaleConfirmation,
    completeTutorial,
    escapeStack,
    trainingModeManager.trainingModeOnboardingState,
  ])

  if (!hasInit) {
    return (
      <div className="vd-card payment-panel--action">
        <LoaderSpinner className="vd-mt10" />
      </div>
    )
  }

  return (
    <div className="vd-card payment-panel--action">
      <Scrollable monobrow={true} goatee={true}>
        <div className="payment-panel-content payment-panel-content--action">
          <div className="sale-confirmation-container">
            <ConfirmationHeader />
            {isQuoteSale ? null : <PrintButtons />}
            <ConfirmationCustomer />
            <MarkAsPickedUpToggle
              isPickedUp={isPickedUp}
              setIsPickedUp={setIsPickedUp}
            />
            {isQuotePaymentRequestFeatureEnabled && isQuoteSale && (
              <QuotePaymentRequestCard
                state={quotePaymentRequest}
                onChange={setQuotePaymentRequest}
              />
            )}
            {isPaymentRequestFeatureEnabled &&
              !isQuoteSale &&
              (isLoadingPaymentRequests ? (
                <LoaderSpinner className="vd-mt4" />
              ) : (
                <PaymentRequestSummaryCard
                  paymentRequests={paymentRequests}
                  onRefetch={refetchPaymentRequests}
                />
              ))}
            {showSaleNote ? (
              <ConfirmationNote
                onErrorsChange={(errors: NoteError[]) =>
                  setSaleNoteHasError(Boolean(errors.length))
                }
              />
            ) : null}
          </div>
        </div>
      </Scrollable>
      <SaleActionBar
        actionDisabled={saleNoteHasError}
        isPickedUp={isPickedUp}
        setIsPickedUp={setIsPickedUp}
        quotePaymentRequest={
          isQuoteSale && isQuotePaymentRequestFeatureEnabled
            ? quotePaymentRequest
            : undefined
        }
      />
    </div>
  )
}

export const ReactConfirmationPanel = reactular(
  ConfirmationPanel,
  [],
  'reactWrapper'
)
